/**
 * Le o TEXTO da etiqueta (saida do OCR) e tira dele o que a portaria precisa.
 *
 * O codigo de barras identifica o objeto, mas nao diz para quem ele vai — o
 * destino esta impresso como texto. Por isso o leitor precisa das duas
 * leituras: a otica, exata, para o rastreio; e a de texto, aproximada, para o
 * apartamento e o nome.
 *
 * Tudo aqui e tolerante a ruido de proposito. OCR troca O por 0, come acento e
 * quebra linha no meio do endereco; um padrao rigido demais simplesmente nao
 * casa e a tela volta a exigir digitacao — que e o problema que viemos
 * resolver. O custo de um palpite errado e baixo porque o porteiro confere na
 * tela antes de registrar.
 */

import { decodeScan, normalizeCode } from './carriers.js';

export interface LabelHints {
  /** Codigo de objeto encontrado no texto (quando o codigo de barras falhou). */
  trackingCode: string | null;
  carrier: string | null;
  /** "1102" — numero do apartamento/casa. */
  unitNumber: string | null;
  /** "B", "02" — bloco ou torre. */
  block: string | null;
  recipient: string | null;
  postalCode: string | null;
}

/** Sem acento, em caixa alta: o OCR erra acento com muita frequencia. */
export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Padroes de endereco brasileiro
// ---------------------------------------------------------------------------

/**
 * Letras que o OCR troca entre si em caixa alta.
 *
 * Nao e paranoia: era o motivo de a etiqueta lida "quase certa" nao render
 * nada. Um `APTO` que sai `APT0` ou `AP7O` nao casa com um padrao literal, e o
 * campo volta vazio como se nada tivesse sido lido — o porteiro ve "nao
 * consegui" e digita tudo, sem saber que faltou um caractere.
 */
const CONFUSABLE: Record<string, string> = {
  A: 'A4',
  B: 'B8',
  E: 'E3',
  G: 'G6',
  I: 'I1L',
  L: 'LI1',
  O: 'O0',
  S: 'S5',
  T: 'T7',
  Z: 'Z2',
};

/** "APTO" vira "[A4]P[T7][O0]" — a mesma palavra, tolerante ao que o OCR erra. */
function fuzzy(word: string): string {
  return [...word]
    .map((letter) => (CONFUSABLE[letter] ? `[${CONFUSABLE[letter]}]` : letter))
    .join('');
}

function keywords(...words: string[]): string {
  return words.map(fuzzy).join('|');
}

// "APTO 1102", "AP. 1102", "APARTAMENTO: 1102", "AP 1102-B", "UNIDADE 34"
const UNIT_PATTERN = new RegExp(
  `\\b(?:${keywords(
    'APARTAMENTO',
    'APART',
    'APTO',
    'APT',
    'AP',
    'UNIDADE',
    'UNID',
    'CASA',
    'CS',
  )})\\b[\\s.:—–\\-Nn°º]*([0-9OILSZB]{1,5}[A-Z]?)\\b`,
);

// "BLOCO B", "BL 02", "TORRE 1", "QD 3"
const BLOCK_PATTERN = new RegExp(
  `\\b(?:${keywords('BLOCO', 'BLC', 'BL', 'TORRE', 'TR', 'QUADRA', 'QD')})\\b[\\s.:—–\\-]*([A-Z0-9]{1,3})\\b`,
);

/** Letra que o OCR pos no lugar de digito, dentro de um numero de unidade. */
const AS_DIGIT: Record<string, string> = { O: '0', I: '1', L: '1', S: '5', Z: '2', B: '8' };

/**
 * Conserta o numero da unidade e recusa o que nao era numero.
 *
 * O padrao aceita letras confundiveis para alcancar o "3O2" que o OCR produz,
 * mas isso faz "UNIDADE B" casar com um `B` solto. Exigir pelo menos um digito
 * DE VERDADE no bruto separa os dois casos: "3O2" vira 302, e "B" e descartado
 * em vez de virar o apartamento 8.
 */
function repairUnitNumber(raw: string | undefined): string | null {
  if (!raw || !/[0-9]/.test(raw)) return null;
  return [...raw].map((character) => AS_DIGIT[character] ?? character).join('');
}

const POSTAL_PATTERN = /\b(\d{5})[\s.-]?(\d{3})\b/;

/** Rotulos que antecedem o nome de quem recebe. */
const RECIPIENT_LABEL = /\b(?:DESTINAT[AÁ]RIO|DESTINATARIO|DEST|RECEBEDOR|NOME|PARA)\b[\s.:—–\-]*/;

/** Linha que e claramente endereco, nao nome de pessoa. */
const ADDRESS_LINE =
  /\b(?:RUA|AV|AVENIDA|TRAVESSA|TRV|ALAMEDA|AL|ROD|RODOVIA|ESTRADA|PRACA|CEP|BAIRRO|CIDADE|UF|N[°º]|NUMERO)\b/;

/** Candidatos a codigo de objeto, tolerando o espaco que o OCR insere. */
const TRACKING_CANDIDATES: RegExp[] = [
  /\b[A-Z]{2}[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?BR\b/g, // SRO/Correios
  /\bTBA[\s.-]?\d{10,}\b/g, // Amazon
  /\b(?:SPX)?BR[\s.-]?\d{10,}\b/g, // Shopee
  /\bML[A-Z]?[\s.-]?\d{8,}\b/g, // Mercado Envios
  /\b\d{44}\b/g, // chave da NF-e
];

/**
 * Nome de pessoa: pelo menos duas palavras so de letras. Filtra "AV PAULISTA"
 * pelo teste de linha de endereco antes de chegar aqui.
 */
function looksLikeName(value: string): boolean {
  const cleaned = value.trim().replace(/\s+/g, ' ');
  if (cleaned.length < 5 || cleaned.length > 80) return false;
  if (/\d/.test(cleaned)) return false;
  if (ADDRESS_LINE.test(cleaned)) return false;
  return cleaned.split(' ').filter((word) => word.length >= 2).length >= 2;
}

function findRecipient(lines: string[]): string | null {
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    const match = RECIPIENT_LABEL.exec(line);
    if (!match) continue;

    // Nome na mesma linha do rotulo ("DESTINATARIO: MARIA SOUZA")...
    const sameLine = line.slice(match.index + match[0].length).trim();
    if (looksLikeName(sameLine)) return sameLine;

    // ...ou logo abaixo dele, que e como a maioria das etiquetas imprime.
    const next = (lines[index + 1] ?? '').trim();
    if (looksLikeName(next)) return next;
  }

  // Sem rotulo algum: a primeira linha que parece nome ainda e um palpite
  // util, porque o destinatario costuma abrir o bloco de endereco.
  return lines.find((line) => looksLikeName(line))?.trim() ?? null;
}

function findTracking(text: string): { code: string; carrier: string | null } | null {
  for (const pattern of TRACKING_CANDIDATES) {
    // `lastIndex` sobrevive entre chamadas em regex global — reiniciamos para
    // que a segunda etiqueta lida nao comece a busca do meio do texto.
    pattern.lastIndex = 0;
    const found = pattern.exec(text);
    if (!found) continue;

    const decoded = decodeScan(normalizeCode(found[0]));
    if (decoded.kind === 'tracking' || decoded.kind === 'invoice') {
      return { code: decoded.value, carrier: decoded.carrier };
    }
  }
  return null;
}

export function parseLabelText(rawText: string): LabelHints {
  const text = normalizeText(rawText);
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const unit = UNIT_PATTERN.exec(text);
  const block = BLOCK_PATTERN.exec(text);
  const postal = POSTAL_PATTERN.exec(text);
  const tracking = findTracking(text);

  return {
    trackingCode: tracking?.code ?? null,
    carrier: tracking?.carrier ?? null,
    unitNumber: repairUnitNumber(unit?.[1]),
    block: block?.[1] ?? null,
    recipient: findRecipient(lines),
    postalCode: postal ? `${postal[1]}-${postal[2]}` : null,
  };
}
